import React, { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';

interface MenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}

interface CustomDropdownMenuProps {
  trigger: React.ReactNode;
  items: MenuItem[];
}

export const CustomDropdownMenu: React.FC<CustomDropdownMenuProps> = ({ trigger, items }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const toggleMenu = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleMenu();
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative block w-full" ref={menuRef}>
      <div 
        onClick={toggleMenu} 
        onKeyDown={handleKeyDown}
        ref={triggerRef} 
        className="focus:outline-none cursor-pointer w-full"
        tabIndex={0}
        role="button"
        aria-haspopup="true"
        aria-expanded={open}
      >
        {trigger}
      </div>
      {open && (
        <div className="absolute right-0 mt-2 min-w-[140px] rounded-md bg-black text-white shadow-lg z-50 p-1" style={{ border: 'none' }}>
          {items.map((item, idx) => (
            <div
              key={item.label + idx}
              className={clsx(
                'flex items-center px-4 py-2 text-sm rounded-md select-none whitespace-nowrap',
                item.className,
                {
                  'opacity-50 pointer-events-none': item.disabled,
                }
              )}
              onClick={() => {
                if (!item.disabled) {
                  item.onClick?.();
                  setOpen(false);
                }
              }}
              tabIndex={0}
              role="menuitem"
              style={{ cursor: item.disabled ? 'not-allowed' : 'pointer' }}
            >
              {item.icon && <span className="mr-2">{item.icon}</span>}
              {item.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}; 